import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { map, mergeMap } from 'rxjs';
import { CartConnector } from '@b2b/features/cart/core/cart.connector';
import {
  addCartItem,
  deleteCart,
  deleteCartItem,
  getCart,
  getCartFail,
  getCartPristine,
  getCartSuccess,
  getLatestCart,
  getMiniCart,
  getOrCreateCart,
  saveCart,
  updateCartItem,
  updateCartItemFail,
  updateCartItemSuccess,
  updateCart,
} from '@b2b/features/cart/core/store/cart.actions';

@Injectable()
export class CartEffects {
  constructor(
    private actions: Actions,
    private cartConnector: CartConnector,
  ) {}

  addCartItem$ = createEffect(() => {
    return this.actions.pipe(
      ofType(addCartItem),
      mergeMap(({ item }) => {
        return this.cartConnector.addCartItem(item).pipe(
          map(cart => {
            if (cart.hasError) {
              return getCartFail({ message: cart.message || '' });
            }

            return getCartSuccess({ data: cart.data! });
          }),
        );
      }),
    );
  });

  updateCart$ = createEffect(() => {
    return this.actions.pipe(
      ofType(updateCart),
      mergeMap(({ name }) => {
        return this.cartConnector.updateCart(name).pipe(
          map(cart => {
            if (cart.hasError) {
              return getCartFail({ message: cart.message || '' });
            }

            return getCartSuccess({ data: cart.data! });
          }),
        );
      }),
    );
  });

  updateCartItem$ = createEffect(() => {
    return this.actions.pipe(
      ofType(updateCartItem),
      mergeMap(({ entryNumber, item }) => {
        return this.cartConnector.updateCartItem(entryNumber, item).pipe(
          map(cart => {
            if (cart.hasError) {
              return updateCartItemFail({ message: cart.message || '' });
            }

            return updateCartItemSuccess({ data: cart.data! });
          }),
        );
      }),
    );
  });

  deleteCartItem$ = createEffect(() => {
    return this.actions.pipe(
      ofType(deleteCartItem),
      mergeMap(({ entryNumber, cartCode }) => {
        return this.cartConnector.deleteCartItem(entryNumber, cartCode).pipe(map(() => getCart({ cartID: cartCode })));
      }),
    );
  });

  deleteAndCreateCart$ = createEffect(() => {
    return this.actions.pipe(
      ofType(deleteCart),
      mergeMap(({ cartCode }) => {
        return this.cartConnector.deleteCart(cartCode).pipe(
          map(cart => {
            if (cart.hasError) {
              return getCartFail({ message: cart.message || '' });
            }

            return getCartPristine();
          }),
        );
      }),
    );
  });

  getCart$ = createEffect(() => {
    return this.actions.pipe(
      ofType(getCart),
      mergeMap(({ cartID }) => {
        return this.cartConnector.get(cartID).pipe(
          map(cart => {
            if (cart.hasError) {
              return getCartFail({ message: cart.message || '' });
            }

            return getCartSuccess({ data: cart.data! });
          }),
        );
      }),
    );
  });

  getMiniCart$ = createEffect(() => {
    return this.actions.pipe(
      ofType(getMiniCart),
      mergeMap(({ cartID }) => {
        return this.cartConnector.getMiniCart(cartID).pipe(
          map(cart => {
            if (cart.hasError) {
              return getCartFail({ message: cart.message || '' });
            }

            return getCartSuccess({ data: cart.data! });
          }),
        );
      }),
    );
  });

  getOrCreateCart$ = createEffect(() => {
    return this.actions.pipe(
      ofType(getOrCreateCart),
      mergeMap(() => {
        return this.cartConnector.getOrCreate().pipe(
          map(cart => {
            if (cart.hasError) {
              return getCartFail({ message: cart.message || '' });
            }

            return getCartSuccess({ data: cart.data! });
          }),
        );
      }),
    );
  });

  getLatestCart$ = createEffect(() => {
    return this.actions.pipe(
      ofType(getLatestCart),
      mergeMap(data => {
        return this.cartConnector.getLatest(data?.customerId).pipe(
          map(cart => {
            if (cart.hasError) {
              return getCartFail({ message: cart.message || '' });
            }

            return getCartSuccess({ data: cart.data! });
          }),
        );
      }),
    );
  });

  saveCart$ = createEffect(() => {
    return this.actions.pipe(
      ofType(saveCart),
      mergeMap(({ name }) => {
        return this.cartConnector.saveCart(name).pipe(
          map(cart => {
            if (cart.hasError) {
              return getCartFail({ message: cart.message || '' });
            }

            return getCartSuccess({ data: cart.data! });
          }),
        );
      }),
    );
  });
}
